import { withFieldOrigin } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValue,
  FieldValues,
} from 'features/libra/ontology/ontologyFieldTypes'
import { FormSectionSchema } from 'features/libra/sections/formSection/formSectionTypes'
import { applyAiFieldValues } from 'features/libra/stepComponents/applyAiFieldValues'
import {
  applyOriginOption,
  FieldOriginOption,
} from 'features/libra/ticket/fieldOrigin'
import { toPersistedShape } from 'features/libra/ticket/fieldValuesPatch'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import type { LibraWorkflowStep } from 'features/libra/workflow/workflowStepsApi'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'

export const niiReportConfigSchema = z.object({
  sections: z.array(FormSectionSchema).optional(),
  fields: z.object({
    analysisDate: z.string(),
    executiveSummary: z.string(),
    internalComments: z.string(),
    eligibility: z.string(),
    eligibilityComputed: z.string(),
    recommendations: z.string(),
    justification: z.string(),
    /** Ontology id for m², e.g. `inspectable_area_m2` */
    inspectableArea: z.string(),
    /** @deprecated Not used in ROI math; traditional cost is derived from group-level rates. Kept optional for backward compat. */
    traditionalInspectionCost: z.string().optional(),
  }),
  zoneFieldName: z.string().default('zones'),
  preScreening: z
    .object({
      stepId: z.string().optional(),
      resultField: z.string(),
    })
    .optional(),
})

export type NiiReportConfig = z.infer<typeof niiReportConfigSchema>
export type NiiReportFields = NiiReportConfig['fields']

/** The workflow step `component` value that identifies an NII Report step. */
export const NII_REPORT_STEP_COMPONENT = 'NiiReport'

/**
 * Finds a workflow's NII Report step (if any) and parses its config. Returns
 * `undefined` when the workflow has no NII Report step, or its config
 * doesn't match {@link niiReportConfigSchema} — the single check used
 * everywhere a ticket/workflow's NII-report capability matters (eligibility
 * filtering, PDF export, the review step's export button).
 */
export function getNiiReportConfig(
  workflowSteps:
    | ReadonlyArray<Pick<LibraWorkflowStep, 'component' | 'config'>>
    | undefined
): NiiReportConfig | undefined {
  const reportStep = workflowSteps?.find(
    (step) => step.component === NII_REPORT_STEP_COMPONENT
  )
  const parsed = niiReportConfigSchema.safeParse(reportStep?.config)
  return parsed.success ? parsed.data : undefined
}

export function useNiiLocalFieldValues(
  fields: NiiReportFields,
  rawDefaults: FieldValues = {}
): {
  localValues: FieldValues
  setLocalFieldValue: (
    key: string,
    value: FieldValue,
    option?: FieldOriginOption
  ) => void
  applyAiLocalFieldValues: (incoming: FieldValues) => void
  hasUnsavedEditsOrSystemValues: boolean
  hasUnsavedEdits: boolean
  doSave: () => Promise<void>
  resetChanges: () => void
  isLoading: boolean
  isSaving: boolean
} {
  const {
    fieldValues: ticketFieldValues,
    setFieldValues: saveToServer,
    isLoading,
  } = useTicketAccess()

  // Defaults are automatic by definition, so they are never `human`.
  const defaults = useMemo<FieldValues>(
    () =>
      Object.fromEntries(
        Object.entries(rawDefaults).map(([key, wrapper]) => [
          key,
          withFieldOrigin(wrapper, 'system'),
        ])
      ),
    [rawDefaults]
  )

  const screeningOwnedKeys = useMemo(
    () => [
      fields.eligibility,
      fields.eligibilityComputed,
      fields.justification,
    ],
    [fields.eligibility, fields.eligibilityComputed, fields.justification]
  )

  const managedKeys = useMemo(() => {
    const readOnlyKeys = new Set(screeningOwnedKeys)
    return Object.values(fields).filter(
      (value): value is string =>
        typeof value === 'string' && !readOnlyKeys.has(value)
    )
  }, [fields, screeningOwnedKeys])

  const serverSnapshot = useMemo(
    () => pick(ticketFieldValues, managedKeys),
    [ticketFieldValues, managedKeys]
  )

  const [localValues, setLocalValues] = useState<FieldValues>(serverSnapshot)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setLocalValues(serverSnapshot)
  }, [serverSnapshot])

  const resolvedValues = useMemo(
    () => ({ ...defaults, ...localValues }),
    [defaults, localValues]
  )

  /** What Save would write (incl. defaults) differs from saved. */
  const hasUnsavedEditsOrSystemValues = useMemo(
    () =>
      !isEqual(
        toPersistedShape(resolvedValues),
        toPersistedShape(serverSnapshot)
      ),
    [resolvedValues, serverSnapshot]
  )

  /** Local values (typed or AI-filled) differ from saved. */
  const hasUnsavedEdits = useMemo(
    () =>
      !isEqual(toPersistedShape(localValues), toPersistedShape(serverSnapshot)),
    [localValues, serverSnapshot]
  )

  const setLocalFieldValue = useCallback(
    (key: string, value: FieldValue, option?: FieldOriginOption) => {
      // Closeout is display-only for screening-owned keys; never stage a write.
      if (screeningOwnedKeys.includes(key)) return
      setLocalValues((prev) =>
        applyOriginOption({ ...prev, [key]: value }, option)
      )
    },
    [screeningOwnedKeys]
  )

  const applyAiLocalFieldValues = useCallback(
    (incoming: FieldValues) => {
      const writable = omit(incoming, screeningOwnedKeys)
      setLocalValues((prev) =>
        applyAiFieldValues({ ...defaults, ...prev }, writable)
      )
    },
    [defaults, screeningOwnedKeys]
  )

  const doSave = useCallback(async () => {
    setIsSaving(true)
    try {
      await saveToServer(omit(resolvedValues, screeningOwnedKeys))
    } finally {
      setIsSaving(false)
    }
  }, [screeningOwnedKeys, saveToServer, resolvedValues])

  const resetChanges = useCallback(() => {
    setLocalValues(serverSnapshot)
  }, [serverSnapshot])

  return {
    localValues: resolvedValues,
    setLocalFieldValue,
    applyAiLocalFieldValues,
    hasUnsavedEditsOrSystemValues,
    hasUnsavedEdits,
    doSave,
    resetChanges,
    isLoading,
    isSaving,
  }
}
